import React, { useEffect, useMemo } from 'react';
import { CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';

function AutoFocusSelected({ selectedId, markerPositions, focusZoom = 15 }) {
  const map = useMap();

  useEffect(() => {
    if (!selectedId) return;
    const key = String(selectedId);
    const pos = markerPositions?.[key] || markerPositions?.[selectedId];
    const lat = Number(pos?.lat);
    const lng = Number(pos?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    map.setView([lat, lng], focusZoom, { animate: true });
  }, [map, selectedId, markerPositions, focusZoom]);

  return null;
}

export default function LeafletDriversMap({
  center,
  zoom = 12,
  driverEntries,
  markerPositions,
  selectedId,
  onSelect,
  truckIconUrl,
  onMapCreated,
  getOpacity,
  renderPopup,
  autoFocusSelected = true,
  focusZoom = 15,
  routeLatLngs,
  routeDest,
  routeColor = '#1677ff',
}) {
  const resolvedTruckUrl = useMemo(() => {
    if (!truckIconUrl) return '';
    if (typeof truckIconUrl === 'string') return truckIconUrl;
    if (typeof truckIconUrl?.default === 'string') return truckIconUrl.default;
    return String(truckIconUrl);
  }, [truckIconUrl]);

  const truckIcon = useMemo(() => {
    if (!resolvedTruckUrl) return undefined;

    // Use divIcon so bundlers/CDNs never break Leaflet image resolution.
    return L.divIcon({
      className: '',
      html: `<img src="${resolvedTruckUrl}" style="width:44px;height:44px;display:block;transform:translate(-50%,-50%);" />`,
      iconSize: [44, 44],
      iconAnchor: [22, 22],
      popupAnchor: [0, -18],
    });
  }, [resolvedTruckUrl]);

  return (
    <MapContainer
      center={[center.lat, center.lng]}
      zoom={zoom}
      style={{ width: '100%', height: '100%' }}
      whenCreated={onMapCreated}
      zoomControl
    >
      {autoFocusSelected && (
        <AutoFocusSelected
          selectedId={selectedId}
          markerPositions={markerPositions}
          focusZoom={focusZoom}
        />
      )}
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {Array.isArray(routeLatLngs) && routeLatLngs.length >= 2 && (
        <Polyline
          positions={routeLatLngs}
          pathOptions={{ color: routeColor, weight: 5, opacity: 0.9 }}
        />
      )}

      {routeDest && Number.isFinite(Number(routeDest?.lat)) && Number.isFinite(Number(routeDest?.lng)) && (
        <CircleMarker
          center={[Number(routeDest.lat), Number(routeDest.lng)]}
          radius={8}
          pathOptions={{ color: '#ff4d4f', fillColor: '#ff4d4f', fillOpacity: 0.9, weight: 2 }}
        />
      )}

      {driverEntries.map(([driverId]) => {
        const key = String(driverId);
        const pos = markerPositions[key] || markerPositions[driverId];
        if (!pos) return null;
        const isSelected = String(selectedId) === key;
        const opacity = typeof getOpacity === 'function' ? getOpacity(driverId) : 1;

        return (
          <Marker
            key={driverId}
            position={[pos.lat, pos.lng]}
            icon={truckIcon}
            opacity={opacity}
            eventHandlers={{
              click: () => onSelect(isSelected ? null : key),
            }}
          >
            {isSelected && (
              <Popup autoPan>
                {typeof renderPopup === 'function' ? renderPopup(driverId) : null}
              </Popup>
            )}
          </Marker>
        );
      })}
    </MapContainer>
  );
}

